import { useTranslation } from "react-i18next";
import { fromJS } from "immutable";
import { Eye } from "lucide-react";
import { UserAgentDeviceBadge } from "../../tracking/UserAgentDeviceBadge";
import { useQuery } from "@tanstack/react-query";
import { format, formatDistanceToNow, isThisYear, isToday, isValid } from "date-fns";
import { getMessageTracking } from "../../api/InboxApi";
import { SmallLoading } from "../../main/Loading";

const TrackingPanel = ({ messageId }: { messageId: string }) => {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ["message-tracking", messageId],
    queryFn: async () => {
      return await getMessageTracking(messageId);
    },
    enabled: !!messageId,
    staleTime: 60 * 1000, // cache 60s
  });

  const renderTrackingView = () => {
    if (isLoading) {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <SmallLoading />
        </div>
      );
    }
    if (!data || data.length === 0) {
      return (
        <div className="w-full h-full flex items-center justify-center">
          {t("No messages to track")}
        </div>
      );
    }

    const pageResult = fromJS(data[0]);
    const geoLocations = pageResult.get("geoLocations") || fromJS([]);
    const items = geoLocations
      .sortBy((item) => new Date(item.get("updatedAt").replace(" ", "T")).getTime())
      .reverse();

    const getLocationInfo = (viewTrackingItem: any) => {
      const city = viewTrackingItem.getIn(["ipLocation", "city"]) || "";
      const region = viewTrackingItem.getIn(["ipLocation", "region"]) || "";
      const countryCode = viewTrackingItem.getIn(["ipLocation", "countryCode"]) || "";
      if (city == "") {
        return t("Unknown location");
      }
      return [city, region, countryCode].filter(Boolean).join(", ");
    };

    const getLocationDateInfo = (viewTrackingItem: any) => {
      const start = viewTrackingItem.getIn(["updatedAt"]);
      const date = start ? new Date(start) : null;
      if (isValid(date)) {
        let timeLabel;
        if (isToday(date)) {
          timeLabel = format(date, "hh:mm a");
        } else if (isThisYear(date)) {
          timeLabel = format(date, "MMM d");
        } else {
          timeLabel = format(date, "MMM d, yyyy");
        }
        const title = format(new Date(start), "PPpp");
        return (
          <div title={title} className="text-right text-[10px] text-gray-600">
            {timeLabel}
          </div>
        );
      }
      return "";
    };

    const hasLocation = items.size > 0;
    if (!hasLocation) {
      const sentAt = new Date(pageResult.get("createdAt"));
      if (isValid(sentAt)) {
        return (
          <div className={"w-full text-center text-gray-600 py-4"}>
            Tracked at {format(sentAt, "PPpp")} - 0 views
          </div>
        );
      }
      return <div className={"w-full text-center text-gray-400 py-4"}>No view</div>;
    }

    let viewInfo = "1 min";
    let title = "1 min";
    if (hasLocation) {
      const start = items.getIn([0, "updatedAt"]);
      const date = new Date(start);
      if (isValid(date)) {
        viewInfo = formatDistanceToNow(date);
        title = format(date, "PPpp");
      }
    }
    return (
      <div>
        <div className="flex flex-col items-center px-4 py-2 border-b border-gray-100">
          <Eye className="w-6 h-6 text-blue-500" />
          <div className="font-semibold" title={title}>
            Viewed {viewInfo} ago
          </div>
          <div className="text-xs text-gray-600">
            {pageResult.get("readCount")} views of last message
          </div>
        </div>
        <div className="text-sm text-gray-600 px-2 pt-2">{items.size} total locations</div>
        <ul className="p-2 overflow-y-auto">
          {items.map((item: any) => {
            const ua = item.get("userAgent") as string | undefined;
            return (
              <li
                key={`tracking_${item.getIn(["ipLocation", "query"])}`}
                className="flex items-center justify-between gap-x-6 py-2"
              >
                <div className="flex items-center space-x-2 min-w-0 flex-1">
                  <div className="flex flex-col items-center shrink-0 gap-1 pt-0.5">
                    <UserAgentDeviceBadge userAgent={ua} className="w-4 h-4" />
                  </div>
                  <div className="text-sm min-w-0 flex-1 pt-0.5">
                    <div>{getLocationInfo(item)}</div>
                  </div>
                </div>
                {getLocationDateInfo(item)}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return renderTrackingView();
};

export default TrackingPanel;
